import { useState, useId } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { PlusCircle, Loader2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { roleApi } from "@/lib/api/services/role.api";
import type { Role, PermissionGroup } from "@/lib/api/admin-api";
import type { CreateRoleInput } from "@/types/user";

interface RoleDialogProps {
  permissionGroups: PermissionGroup[];
  role?: Role;
  trigger?: React.ReactNode;
  onClose?: () => void;
  onDelete?: () => void;
  deleteLoading?: boolean;
  createFn?: (data: CreateRoleInput) => Promise<Role>;
}

interface FormData {
  name: string;
  code: string;
  description: string;
  permissions: string[];
}

function toCode(name: string) {
  return name.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");
}

export function RoleDialog({
  permissionGroups,
  role,
  trigger,
  onClose,
  onDelete,
  deleteLoading = false,
  createFn,
}: RoleDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const idPrefix = useId();
  const isEdit = !!role;
  const isProtectedRole = !!role?.isSystem;

  const [isOpen, setIsOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({
    name: role?.name ?? "",
    code: role?.code ?? "",
    description: role?.description ?? "",
    permissions: (role?.permissions as string[]) ?? [],
  });

  const mutation = useMutation({
    mutationFn: (data: FormData) => {
      if (isEdit) {
        return roleApi.update(role!.id, {
          name: data.name.trim(),
          description: data.description.trim() || undefined,
          permissions: data.permissions,
        });
      }
      const fn = createFn ?? roleApi.create;
      return fn({
        name: data.name.trim(),
        code: data.code.trim(),
        description: data.description.trim() || undefined,
        permissions: data.permissions,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast.success(isEdit ? t`Role updated` : t`Role created`);
      handleOpenChange(false);
      onClose?.();
    },
    onError: (error: Error) => {
      setSubmitError(error.message || (isEdit ? t`Failed to update role` : t`Failed to create role`));
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSubmitError(null);
      if (!isEdit) {
        setForm({ name: "", code: "", description: "", permissions: [] });
      }
    }
    setIsOpen(open);
  };

  const togglePermission = (perm: string) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (isProtectedRole) {
      setSubmitError(t`This default role cannot be modified.`);
      return;
    }
    setSubmitError(null);
    mutation.mutate(form);
  };

  const defaultTrigger = isEdit ? (
    <Button variant="outline" size="sm">
      {t`Edit`}
    </Button>
  ) : (
    <Button className="gap-2">
      <PlusCircle className="h-4 w-4" />
      {t`New Role`}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger ?? defaultTrigger}</DialogTrigger>

      <DialogContent
        hideClose
        className="max-w-[720px] p-0 overflow-hidden gap-0 border-none rounded-(--radius-lg)"
      >
        <div className="flex flex-col bg-(--color-surface-content) max-h-[90vh]">
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-(--color-border)">
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-none">
                {isEdit ? t`Edit Role` : t`Create Role`}
              </DialogTitle>
              <p className="text-sm text-(--color-text-secondary)">
                {isEdit
                  ? t`Update the role's name, description, and permissions.`
                  : t`Define a custom role with a specific set of permissions.`}
              </p>
              {isProtectedRole && (
                <p className="text-xs text-(--color-text-secondary) mt-1">
                  {t`This is a protected default role and cannot be edited or deleted.`}
                </p>
              )}
            </div>
            <DialogClose
              aria-label={t`Close`}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <X className="h-6 w-6" />
            </DialogClose>
          </div>

          <form
            id={`${idPrefix}-form`}
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-y-auto"
          >
            {/* Basic fields */}
            <div className="p-5 border-b border-(--color-border) flex flex-col gap-4">
              <div className="flex gap-3">
                <div className="flex flex-col gap-1.5 flex-1">
                  <Label htmlFor={`${idPrefix}-name`}>{t`Name`}</Label>
                  <Input
                    id={`${idPrefix}-name`}
                    required
                    disabled={isProtectedRole}
                    placeholder={t`e.g. Kitchen Lead`}
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        name,
                        code: isEdit ? prev.code : toCode(name),
                      }));
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <Label htmlFor={`${idPrefix}-code`}>{t`Code`}</Label>
                  <Input
                    id={`${idPrefix}-code`}
                    required
                    disabled={isEdit}
                    placeholder="KITCHEN_LEAD"
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                    }
                    className="font-mono"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${idPrefix}-desc`}>{t`Description`}</Label>
                <Input
                  id={`${idPrefix}-desc`}
                    disabled={isProtectedRole}
                  placeholder={t`Optional description…`}
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Permission picker */}
            <div className="p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">{t`Permissions`}</h3>
                <Badge variant="neutral">{form.permissions.length} {t`selected`}</Badge>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {permissionGroups.map((group) => (
                  <div key={group.resource}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-(--color-text-secondary) mb-2">
                      {group.resource}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {group.actions.map((action) => {
                        const perm = `${group.resource}:${action}`;
                        const id = `${idPrefix}-${perm}`;
                        return (
                          <div
                            key={perm}
                            className="flex items-center gap-2 cursor-pointer select-none"
                          >
                            <Switch
                              id={id}
                              disabled={isProtectedRole}
                              checked={form.permissions.includes(perm)}
                              onCheckedChange={() => togglePermission(perm)}
                            />
                            <Label htmlFor={id} className="text-sm font-mono cursor-pointer">
                              {action}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="p-5 border-t border-(--color-border) flex flex-col gap-3 bg-(--color-surface-content) shrink-0">
            {submitError && (
              <div className="flex items-center gap-2 rounded-lg bg-(--color-danger)/10 border border-(--color-danger)/20 px-4 py-2.5 text-sm text-(--color-danger-text)">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex justify-end gap-3">
              {isEdit && onDelete && !isProtectedRole && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={mutation.isPending || deleteLoading}
                  onClick={onDelete}
                  className="w-32 mr-auto"
                >
                  {t`Delete`}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="w-32"
              >
                {t`Cancel`}
              </Button>
              <Button
                form={`${idPrefix}-form`}
                type="submit"
                disabled={mutation.isPending || isProtectedRole}
                className="w-32"
              >
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? t`Save` : t`Create`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
